import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { Reorder, useDragControls } from 'framer-motion';
import { generateBracket } from '../../features/bracket/bracketSlice';
import {
  BracketTypeCard,
  BracketWizard,
  MatchSelect,
  WizardHint
} from './bracketWizard';
import {
  buildPlayoffMatches,
  buildPlayoffRounds,
  buildSectionMatches,
  drawPlayoffMatches,
  fillPlayoffMatches,
  matchesLabel,
  newPair,
  pairInOrder,
  roundsLabel,
  shuffle,
  summarizeSectionPairings
} from './bracketPairings';
import { defaultSectionType, findSectionType, sectionTypeOptions } from './bracketTypes';

// The pairings a type opens with: every section paired for a standard tree, or
// just the play-off rows with the rest going straight into the stage round.
const matchesForType = (typeValue, sections) => {
  const shape = findSectionType(typeValue, sections.length);
  return shape?.playoffs
    ? buildPlayoffMatches(sections, shape.initialMatches)
    : buildSectionMatches(sections);
};

const PairingRow = ({
  pair,
  index,
  isLast,
  options1,
  options2,
  onSideChange,
  onRemove
}) => {
  const controls = useDragControls();

  return (
    <Reorder.Item
      as="div"
      value={pair}
      dragListener={false}
      dragControls={controls}
      style={{ marginBottom: isLast ? 0 : 8 }}
    >
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1.25 },
          p: { xs: 1, sm: 1.25 },
          borderRadius: 2.5,
          bgcolor: 'background.paper'
        }}
      >
        <Box
          onPointerDown={(event) => controls.start(event)}
          sx={{ display: 'flex', flexShrink: 0, cursor: 'grab', color: 'text.disabled', touchAction: 'none' }}
          aria-label={`Reorder match ${index + 1}`}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Typography variant="caption" sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}>
          {index + 1}
        </Typography>
        <MatchSelect
          value={pair.section1Id}
          onChange={(value) => onSideChange(pair.id, 'section1Id', value)}
          placeholder="Select section"
          options={options1}
        />
        <Typography variant="caption" sx={{ flexShrink: 0, fontWeight: 600, color: 'text.secondary' }}>
          vs
        </Typography>
        <MatchSelect
          value={pair.section2Id}
          onChange={(value) => onSideChange(pair.id, 'section2Id', value)}
          placeholder="Select section"
          options={options2}
        />
        <IconButton
          size="small"
          onClick={() => onRemove(pair.id)}
          aria-label={`Remove match ${index + 1}`}
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Reorder.Item>
  );
};

const GenerateBracketFromSections = ({ event, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const generating = useSelector((state) => state.bracket.generating);
  const sections = useMemo(() => event.sections || [], [event.sections]);
  const [name, setName] = useState(`${event.name || 'Event'} Bracket`);
  const [type, setType] = useState(() => defaultSectionType(sections.length));
  const [activeStep, setActiveStep] = useState(0);
  const [matches, setMatches] = useState(() => matchesForType(type, sections));
  const [submitError, setSubmitError] = useState('');

  const selectedType = findSectionType(type, sections.length);
  const playoffs = Boolean(selectedType?.playoffs);

  useEffect(() => {
    setMatches(matchesForType(type, sections));
  }, [type, sections]);

  const sectionById = useMemo(
    () => new Map(sections.map((section) => [section.id, section])),
    [sections]
  );
  const sectionName = (sectionId) => {
    const section = sectionById.get(sectionId);
    return section ? section.name || 'Unnamed section' : 'Section';
  };

  const { duplicateIds, completePairs, unpaired } = summarizeSectionPairings(sections, matches);
  const pairCountOk = Boolean(selectedType) && matches.length === selectedType.initialMatches;
  const pairingsValid =
    pairCountOk &&
    completePairs === matches.length &&
    !duplicateIds &&
    (playoffs || unpaired.length === 0);
  const canSetupContinue = Boolean(name.trim()) && Boolean(selectedType);
  const canContinue = activeStep === 0 ? canSetupContinue : pairingsValid;

  const pairingMessage = (() => {
    if (!selectedType) {
      return 'Choose a bracket size first.';
    }
    if (!pairCountOk) {
      return playoffs
        ? `This draw needs ${selectedType.initialMatches} play-off matches; you have ${matches.length}.`
        : `This bracket needs ${selectedType.initialMatches} first-round matches; you have ${matches.length}.`;
    }
    if (duplicateIds) {
      return 'A section is used in more than one match.';
    }
    if (!playoffs && unpaired.length > 0) {
      const names = unpaired.slice(0, 3).map((section) => section.name).join(', ');
      return `Pair the remaining ${unpaired.length} section${unpaired.length === 1 ? '' : 's'}${names ? ` (${names}${unpaired.length > 3 ? '…' : ''})` : ''}.`;
    }
    if (completePairs !== matches.length) {
      return 'Every match needs two sections.';
    }
    return '';
  })();

  const updateSide = (pairId, side, value) => {
    setMatches((current) =>
      current.map((pair) => {
        if (pair.id !== pairId) {
          return pair;
        }

        const next = { ...pair, [side]: value };
        const other = side === 'section1Id' ? 'section2Id' : 'section1Id';
        if (value && next[other] === value) {
          next[other] = '';
        }
        return next;
      })
    );
  };

  const removePair = (pairId) => setMatches((current) => current.filter((pair) => pair.id !== pairId));

  const addPair = () => {
    if (!selectedType || matches.length >= selectedType.initialMatches) {
      return;
    }
    setMatches((current) => [...current, newPair('', '')]);
  };

  const autoPair = () =>
    setMatches(
      playoffs
        ? fillPlayoffMatches(sections, matches, selectedType.initialMatches)
        : pairInOrder(sections)
    );
  const randomDraw = () =>
    setMatches(
      playoffs
        ? drawPlayoffMatches(sections, selectedType.initialMatches)
        : pairInOrder(shuffle(sections))
    );
  const resetPairings = () => setMatches(matchesForType(type, sections));

  const optionsFor = (pair, side) => {
    const usedElsewhere = new Set();
    matches.forEach((entry) => {
      if (entry.id === pair.id) {
        return;
      }
      if (entry.section1Id) {
        usedElsewhere.add(entry.section1Id);
      }
      if (entry.section2Id) {
        usedElsewhere.add(entry.section2Id);
      }
    });

    const otherSide = side === 'section1Id' ? 'section2Id' : 'section1Id';

    return sections
      .filter((section) => !usedElsewhere.has(section.id) && section.id !== pair[otherSide])
      .map((section) => ({ value: section.id, label: section.name || 'Unnamed section' }));
  };

  const handleGenerate = () => {
    if (!selectedType || !pairingsValid) {
      return;
    }

    setSubmitError('');

    const payload = playoffs
      ? {
          eventId: event.id,
          name: name.trim(),
          type: 'Custom',
          rounds: buildPlayoffRounds(selectedType, sections, matches)
        }
      : {
          eventId: event.id,
          name: name.trim(),
          type,
          orderedSections: matches.map((pair) => ({
            section1Id: pair.section1Id,
            section2Id: pair.section2Id
          }))
        };

    dispatch(generateBracket(payload))
      .unwrap()
      .then((bracket) => {
        if (!bracket || !bracket.id) {
          setSubmitError('The bracket could not be created. Check the sections and try again.');
          return;
        }
        onSuccess();
      })
      .catch((message) => {
        setSubmitError(
          typeof message === 'string' && message
            ? message
            : 'The bracket could not be created. Please try again.'
        );
      });
  };

  const setupStep = (
    <Stack spacing={2.5}>
      <TextField
        label="Bracket name"
        value={name}
        onChange={(changeEvent) => setName(changeEvent.target.value)}
        fullWidth
        size="small"
        helperText="Shown on the bracket page and in exports."
      />

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Bracket size
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {playoffs
                ? 'A play-off round feeding a standard knockout tree.'
                : "A standard knockout tree for this event's sections."}
            </Typography>
          </Box>
          <Chip size="small" label={`${sections.length} sections`} />
        </Stack>

        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(auto-fit, minmax(170px, 1fr))'
            },
            gap: 1.5
          }}
        >
          {sectionTypeOptions(sections.length).map(({ type: entry, disabled }) => (
            <BracketTypeCard
              key={entry.value}
              selected={type === entry.value}
              disabled={disabled}
              label={entry.label}
              meta={
                entry.playoffs
                  ? `${entry.initialMatches} play-offs · ${entry.directSections} direct`
                  : `${entry.requiredSections} sections · ${entry.initialMatches} first-round matches`
              }
              roundsLabel={roundsLabel(entry.rounds)}
              onSelect={() => setType(entry.value)}
            />
          ))}
        </Box>

        {playoffs && (
          <WizardHint>
            {selectedType.initialMatches} play-offs feed the {selectedType.stageLabel}; the other{' '}
            {selectedType.directSections} sections go straight in.
          </WizardHint>
        )}
      </Box>

      {!selectedType && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          This event has {sections.length} sections, which does not fit a standard bracket or the
          play-offs shape. Ask the assistant to build a custom tree from the draw sheets, or adjust
          the sections first.
        </Alert>
      )}

      {selectedType && (
        <Box sx={{ p: 1.75, border: 1, borderColor: 'divider', borderRadius: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Round plan
            </Typography>
            <Chip size="small" label={`${selectedType.rounds.length} rounds`} />
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {selectedType.rounds.map((round) => (
              <Chip
                key={round.name}
                size="small"
                variant="outlined"
                label={`${round.name} · ${round.matches}`}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const pairingsStep = selectedType ? (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={1.5}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {playoffs ? 'Seed the play-offs' : 'Pair the sections'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {playoffs
              ? `${selectedType.initialMatches} play-offs feed the ${selectedType.stageLabel}; the other ${selectedType.directSections} sections go straight in. Drag to set the play-off order.`
              : 'Drag to set the bracket order. Each section must appear exactly once.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={autoPair}>
            Auto-pair
          </Button>
          <Button size="small" variant="outlined" startIcon={<ShuffleIcon />} onClick={randomDraw}>
            Random draw
          </Button>
          <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetPairings}>
            Reset
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={
            (playoffs ? completePairs === matches.length : unpaired.length === 0) && !duplicateIds
              ? 'success'
              : 'default'
          }
          variant={
            (playoffs ? completePairs === matches.length : unpaired.length === 0) && !duplicateIds
              ? 'filled'
              : 'outlined'
          }
          icon={
            (playoffs ? completePairs === matches.length : unpaired.length === 0) && !duplicateIds ? (
              <CheckIcon />
            ) : undefined
          }
          label={
            playoffs
              ? `${completePairs} of ${selectedType.initialMatches} play-offs paired`
              : `${sections.length - unpaired.length} of ${sections.length} sections paired`
          }
        />
        <Chip
          size="small"
          variant="outlined"
          label={
            playoffs
              ? `${matches.length} of ${selectedType.initialMatches} play-offs`
              : `${matches.length} of ${selectedType.initialMatches} matches`
          }
        />
        {playoffs && (
          <Chip
            size="small"
            variant="outlined"
            label={`${unpaired.length} direct to ${selectedType.stageLabel}`}
          />
        )}
        {duplicateIds && <Chip size="small" color="error" label="Duplicate section" />}
      </Stack>

      <Box>
        <Reorder.Group
          as="div"
          axis="y"
          values={matches}
          onReorder={(next) => setMatches(next)}
          style={{ margin: 0, padding: 0 }}
        >
          {matches.map((pair, index) => (
            <PairingRow
              key={pair.id}
              pair={pair}
              index={index}
              isLast={index === matches.length - 1}
              options1={optionsFor(pair, 'section1Id')}
              options2={optionsFor(pair, 'section2Id')}
              onSideChange={updateSide}
              onRemove={removePair}
            />
          ))}
        </Reorder.Group>

        {matches.length < selectedType.initialMatches && (
          <Button size="small" startIcon={<AddIcon />} onClick={addPair} sx={{ mt: 1 }}>
            {playoffs ? 'Add play-off' : 'Add match'}
          </Button>
        )}

        <WizardHint>
          {playoffs
            ? `Drag to reorder the play-offs. Their winners are spread across the ${selectedType.stageLabel}; sections not in a play-off go straight into it.`
            : 'Drag the handle to change which side of the bracket a match sits on.'}
        </WizardHint>
      </Box>

      {!pairingsValid && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          {pairingMessage}
        </Alert>
      )}
    </Stack>
  ) : (
    <Alert severity="warning">Choose a bracket size before pairing sections.</Alert>
  );

  const reviewStep = selectedType ? (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundImage: 'linear-gradient(135deg, rgba(255,235,59,0.16), rgba(255,235,59,0))'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'yellow.main',
              color: '#18181b'
            }}
          >
            <AccountTreeIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
              {name.trim()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedType.label} · {event.name}
            </Typography>
          </Box>
          <Chip size="small" color="success" variant="outlined" label="Ready" />
        </Stack>
      </Paper>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Rounds
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {matchesLabel(selectedType.rounds)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {selectedType.rounds.map((round, index) => (
            <Chip
              key={round.name}
              size="small"
              label={`${round.name} · ${round.matches}`}
              sx={{ bgcolor: index === 0 ? 'action.selected' : 'transparent' }}
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {playoffs ? 'Play-offs' : 'First round'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top to bottom in bracket order
          </Typography>
        </Stack>
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          {matches.map((pair, index) => (
            <Box
              key={pair.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderBottom: index < matches.length - 1 ? 1 : 0,
                borderColor: 'divider'
              }}
            >
              <Typography
                variant="caption"
                sx={{ width: 20, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}
              >
                {index + 1}
              </Typography>
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
                noWrap
              >
                {sectionName(pair.section1Id)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                vs
              </Typography>
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 0, fontWeight: 600, textAlign: 'right' }}
                noWrap
              >
                {sectionName(pair.section2Id)}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      {playoffs && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Straight to {selectedType.stageLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unpaired.length} sections
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {unpaired.map((section) => (
              <Chip
                key={section.id}
                size="small"
                variant="outlined"
                label={section.name || 'Unnamed section'}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        {playoffs
          ? `Generating creates the full tree. Play-off winners fill their ${selectedType.stageLabel} slots as the play-offs finish; the direct sections are placed already.`
          : 'Generating creates the full tree. Section winners fill their later-round slots as the sections finish.'}
      </Typography>
    </Stack>
  ) : (
    <Alert severity="warning">Choose a bracket size first.</Alert>
  );

  return (
    <BracketWizard
      icon={<AccountTreeIcon />}
      eventName={event.name}
      steps={playoffs ? ['Setup', 'Play-offs', 'Review'] : ['Setup', 'Pairings', 'Review']}
      activeStep={activeStep}
      onStepClick={(index) => setActiveStep(index)}
      error={submitError}
      generating={generating}
      canContinue={canContinue}
      canGenerate={pairingsValid}
      onCancel={onCancel}
      onBack={() => setActiveStep((step) => Math.max(0, step - 1))}
      onNext={() => setActiveStep((step) => Math.min(2, step + 1))}
      onGenerate={handleGenerate}
    >
      {activeStep === 0 && setupStep}
      {activeStep === 1 && pairingsStep}
      {activeStep === 2 && reviewStep}
    </BracketWizard>
  );
};

export default GenerateBracketFromSections;
